/**
 * SchoolWebsitePage — the public page of an FWM school, from kindergarten to
 * university. Public by default; Apply Now is the loudest thing on the page.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ArrowRight, LogIn, GraduationCap, UserCog, Heart, Shield, Menu, X,
  Download,
} from 'lucide-react';
import EnquiryBand from '@/components/school/EnquiryBand';
import {
  StatsStrip, AboutBand, ProgrammesBand, FacilitiesBand, AdmissionsBand, NewsBand,
  TestimonialsBand, GalleryBand, ContactBand, CustomBands, SCHOOL_TYPES,
} from '@/components/school/PublicSchoolSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_FALLBACKS = {
  university: 'https://images.unsplash.com/photo-1705354154486-110e7677db74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwc3R1ZGVudHMlMjBsZWFybmluZyUyMGxhYm9yYXRvcnklMjBsaWJyYXJ5fGVufDB8fHx8MTc4ODM5NTk3MXww&ixlib=rb-4.1.0&q=85',
  college: 'https://images.unsplash.com/photo-1758270704524-596810e891b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxzY2hvb2wlMjBzdHVkZW50cyUyMGNsYXNzcm9vbSUyMGxlYXJuaW5nJTIwY2FtcHVzfGVufDB8fHx8MTc4ODM5NTk3MHww&ixlib=rb-4.1.0&q=85',
  bible_college: 'https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwc3R1ZGVudHMlMjBsZWFybmluZyUyMGxhYm9yYXRvcnklMjBsaWJyYXJ5fGVufDB8fHx8MTc4ODM5NTk3MXww&ixlib=rb-4.1.0&q=85',
  default: 'https://images.unsplash.com/photo-1758270704226-db897b180243?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzY2hvb2wlMjBzdHVkZW50cyUyMGNsYXNzcm9vbSUyMGxlYXJuaW5nJTIwY2FtcHVzfGVufDB8fHx8MTc4ODM5NTk3MHww&ixlib=rb-4.1.0&q=85',
};

const PORTALS = [
  { id: 'teacher', label: 'Teacher / Staff', icon: UserCog },
  { id: 'student', label: 'Student', icon: GraduationCap },
  { id: 'parent', label: 'Parent', icon: Heart },
  { id: 'admin', label: 'Admin', icon: Shield },
];

const SchoolWebsitePage = () => {
  const { tenantId } = useParams();
  const [navOpen, setNavOpen] = useState(false);

  const { data: school, isLoading, error } = useQuery({
    queryKey: ['school-website', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/website`);
      if (!res.ok) throw new Error('School not found');
      return res.json();
    },
    enabled: !!tenantId,
  });

  useEffect(() => {
    const id = 'fw-school-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4 px-6 text-center">
        <h1 className="fw-display text-2xl font-bold text-slate-900">This page isn’t available</h1>
        <p className="text-slate-500 text-sm">The school may have unpublished it, or the link is wrong.</p>
        <Link to="/"><Button variant="outline">Go home</Button></Link>
      </div>
    );
  }

  const brand = school.primary_color || '#1E3A8A';
  const abs = (u) => (u ? (u.startsWith('http') ? u : `${API_URL}${u}`) : null);
  const logoUrl = abs(school.logo_url);
  const heroUrl = abs(school.hero_image_url)
    || HERO_FALLBACKS[school.school_type_key] || HERO_FALLBACKS.default;
  const typeLabel = SCHOOL_TYPES[school.school_type_key] || SCHOOL_TYPES[school.type] || 'School';
  const applyUrl = school.apply_url || `/school/${tenantId}/apply`;
  const cta = school.hero_cta_label || 'Apply Now';

  const anchors = [
    (school.about_us || school.mission || school.vision) && ['about', 'About'],
    ['programmes', 'Programmes'],
    school.facilities?.length > 0 && ['facilities', 'Facilities'],
    ['admissions', 'Admissions'],
    school.announcements?.length > 0 && ['news', 'News'],
    school.testimonials?.length > 0 && ['testimonials', 'Voices'],
    school.gallery_urls?.length > 0 && ['gallery', 'Gallery'],
    ['enquire', 'Enquire'],
    ['contact', 'Contact'],
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white fw-page" data-testid="school-website-page">
      <style>{`
        .fw-page { font-family: 'DM Sans', ui-sans-serif, system-ui, sans-serif; }
        .fw-display { font-family: 'Fraunces', 'DM Sans', Georgia, serif; }
        .fw-reveal { opacity: 0; transform: translateY(22px);
          transition: opacity .7s cubic-bezier(.16,1,.3,1), transform .7s cubic-bezier(.16,1,.3,1); }
        .fw-reveal.is-revealed { opacity: 1; transform: none; }
        .fw-stagger { animation: fwUp .7s cubic-bezier(.16,1,.3,1) both; }
        @keyframes fwUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) {
          .fw-reveal, .fw-stagger { opacity: 1 !important; transform: none !important; animation: none !important; }
        }
      `}</style>

      {/* ── Sticky glass navigation ── */}
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl"
              data-testid="school-nav">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 h-[72px] flex items-center gap-6">
          <a href="#top" className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={school.name} className="h-9 w-9 object-contain rounded-lg" />
            ) : (
              <span className="h-9 w-9 rounded-lg grid place-items-center text-white text-xs font-bold shrink-0"
                    style={{ backgroundColor: brand }}>
                {school.name.slice(0, 2).toUpperCase()}
              </span>
            )}
            <span className="min-w-0">
              <span className="fw-display block text-sm font-bold text-slate-900 truncate max-w-[190px] sm:max-w-none">
                {school.name}
              </span>
              <span className="hidden sm:block text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-400">
                {typeLabel}
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-6 ml-auto text-sm">
            {anchors.map(([id, label]) => (
              <a key={id} href={`#${id}`} data-testid={`nav-${id}`}
                 className="text-slate-500 hover:text-slate-900 transition-colors">{label}</a>
            ))}
          </nav>

          <div className="flex items-center gap-2 ml-auto lg:ml-0">
            <Link to={`/school/${tenantId}/login`} className="hidden sm:block">
              <Button variant="ghost" size="sm" data-testid="portal-login-nav-btn"
                      className="text-slate-600 hover:text-slate-900">
                <LogIn className="h-4 w-4 mr-1.5" /> Portal
              </Button>
            </Link>
            <Link to={applyUrl}>
              <Button size="sm" data-testid="apply-now-nav-btn"
                      className="rounded-full px-5 font-semibold text-white hover:brightness-110 active:scale-95 transition-all"
                      style={{ backgroundColor: brand }}>
                {cta}
              </Button>
            </Link>
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setNavOpen((v) => !v)}
                    data-testid="school-nav-toggle" aria-label="Menu">
              {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {navOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 grid gap-3"
               data-testid="school-nav-mobile">
            {anchors.map(([id, label]) => (
              <a key={id} href={`#${id}`} onClick={() => setNavOpen(false)}
                 className="text-sm text-slate-600">{label}</a>
            ))}
            <Link to={`/school/${tenantId}/login`} className="text-sm font-semibold" style={{ color: brand }}>
              Portal sign-in
            </Link>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section id="top" data-testid="school-hero-section" className="relative overflow-hidden bg-white">
        <div className="absolute -top-40 -right-24 h-[34rem] w-[34rem] rounded-full blur-3xl opacity-[0.13] pointer-events-none"
             style={{ backgroundColor: brand }} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24
                        grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 fw-stagger">
            <Badge variant="outline" className="border-0 text-[11px] font-semibold tracking-wide"
                   style={{ backgroundColor: `${brand}14`, color: brand }} data-testid="school-type-badge">
              {typeLabel}
            </Badge>
            <h1 className="fw-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight
                           text-slate-900 leading-[1.05]" data-testid="school-name">
              {school.hero_title || school.name}
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="school-tagline">
              {school.hero_tagline
                || (school.about_us
                  ? `${school.about_us.slice(0, 180)}${school.about_us.length > 180 ? '…' : ''}`
                  : 'A place to learn, to belong and to be sent out well prepared.')}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to={applyUrl}>
                <Button size="lg" data-testid="apply-now-hero-btn"
                        className="rounded-full px-9 h-12 text-base font-semibold text-white shadow-[0_16px_40px_-16px_rgba(15,23,42,0.55)]
                                   hover:brightness-110 active:scale-95 transition-all"
                        style={{ backgroundColor: brand }}>
                  {cta} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href={`${API_URL}/api/public/school/tenants/${tenantId}/prospectus.pdf`}
                 target="_blank" rel="noreferrer">
                <Button size="lg" variant="outline" data-testid="download-prospectus-btn"
                        className="rounded-full px-8 h-12 text-base font-semibold border-slate-300 text-slate-700
                                   hover:border-slate-900 hover:text-slate-900">
                  <Download className="h-4 w-4 mr-2" /> Prospectus
                </Button>
              </a>
              <a href="#programmes">
                <Button size="lg" variant="ghost" data-testid="explore-programmes-btn"
                        className="rounded-full px-8 h-12 text-base font-semibold border-slate-300 text-slate-700
                                   hover:border-slate-900 hover:text-slate-900">
                  Explore programmes
                </Button>
              </a>
            </div>
            {school.established_year && (
              <p className="mt-8 text-xs font-mono text-slate-400" data-testid="school-established">
                {`Est. ${school.established_year}`}{school.code ? `  ·  ${school.code}` : ''}
              </p>
            )}
          </div>

          <div className="lg:col-span-6 fw-stagger" style={{ animationDelay: '120ms' }}>
            <div className="relative">
              <div className="overflow-hidden rounded-[28px] border border-slate-200 shadow-[0_40px_120px_-60px_rgba(15,23,42,0.6)]">
                <img src={heroUrl} alt={school.name} data-testid="school-hero-image"
                     className="h-[300px] sm:h-[420px] w-full object-cover" />
              </div>
              <div className="mt-4 sm:w-[340px] sm:-mt-10 sm:ml-8 relative
                              rounded-2xl bg-white border border-slate-200 shadow-xl p-5"
                   data-testid="hero-float-card">
                <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400">
                  Applications
                </p>
                <p className="mt-1.5 text-sm text-slate-700 leading-snug">
                  {school.intakes?.length
                    ? `${school.intakes[0].name} is open — apply online in minutes.`
                    : 'Apply online and the admissions office will be in touch.'}
                </p>
                <Link to={applyUrl} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold"
                      style={{ color: brand }} data-testid="hero-card-apply-link">
                  Start your application <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="h-10" />
      </section>

      <StatsStrip stats={school.stats || {}} brand={brand} />
      <AboutBand school={school} brand={brand} typeLabel={typeLabel} />
      <ProgrammesBand programmes={school.programmes || []} brand={brand} applyUrl={applyUrl} />
      <FacilitiesBand facilities={school.facilities || []} note={school.facilities_note} brand={brand} />
      <AdmissionsBand school={school} brand={brand} applyUrl={applyUrl} />
      <NewsBand announcements={school.announcements} brand={brand} />
      <TestimonialsBand testimonials={school.testimonials || []} brand={brand} />
      <GalleryBand urls={school.gallery_urls || []} brand={brand} apiUrl={API_URL} />
      <CustomBands sections={school.custom_sections} brand={brand} />
      <EnquiryBand tenantId={tenantId} programmes={school.programmes || []} brand={brand} />
      <ContactBand school={school} brand={brand} />

      {/* ── Closing call to action ── */}
      <section className="bg-slate-950 py-20" data-testid="school-final-cta">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 flex flex-col lg:flex-row items-start lg:items-center gap-8 justify-between">
          <div>
            <h2 className="fw-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Ready to join {school.name}?
            </h2>
            <p className="mt-3 text-white/60">Applications are online and take a few minutes to start.</p>
          </div>
          <Link to={applyUrl}>
            <Button size="lg" data-testid="apply-now-cta-btn"
                    className="rounded-full px-9 h-12 text-base font-semibold text-white hover:brightness-110 active:scale-95 transition-all"
                    style={{ backgroundColor: brand }}>
              {cta} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 border-t border-white/5 py-14" data-testid="school-footer">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <p className="fw-display text-white font-bold">{school.name}</p>
            <p className="mt-2 text-xs text-white/40">{typeLabel}</p>
            <p className="mt-6 text-xs text-white/40 leading-relaxed">
              Part of the Faith World Ministries family of schools and colleges.
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/40">Portals</p>
            <div className="mt-4 grid gap-2.5">
              {PORTALS.map((p) => (
                <Link key={p.id} data-testid={`portal-link-${p.id}`}
                      to={p.id === 'admin' ? `/login?tenant=${tenantId}` : `/school/${tenantId}/login?type=${p.id}`}
                      className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2">
                  <p.icon className="h-3.5 w-3.5" /> {p.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-white/40">Admissions</p>
            <div className="mt-4 grid gap-2.5 text-sm">
              <Link to={applyUrl} className="text-white/60 hover:text-white transition-colors"
                    data-testid="footer-apply-link">Apply online</Link>
              <a href={`${API_URL}/api/public/school/tenants/${tenantId}/prospectus.pdf`}
                 target="_blank" rel="noreferrer" data-testid="footer-prospectus-link"
                 className="text-white/60 hover:text-white transition-colors">Download prospectus</a>
              <a href="#admissions" className="text-white/60 hover:text-white transition-colors">Intake dates</a>
              <a href="#contact" className="text-white/60 hover:text-white transition-colors">Contact admissions</a>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-12 pt-8 border-t border-white/5
                        text-xs text-white/30 flex flex-wrap gap-3 justify-between">
          <span>© {new Date().getFullYear()} {school.name}. All rights reserved.</span>
          <span>Faith World Ministries</span>
        </div>
      </footer>
    </div>
  );
};

export default SchoolWebsitePage;
